import type * as ecModels from '../../../../models'

import type { CreationId } from './creation-id'

/**
 * Identifies a set of edits/deletions to submit
 */
export interface EditsSelection {
  /** @see {@link EditsSelection.Of}*/
  create?: EditsSelection.Of<CreationId>
  /** @see {@link EditsSelection.Of}*/
  update?: EditsSelection.Of<ecModels.EntityId>
  /** @see {@link EditsSelection.Of}*/
  delete?: EditsSelection.Of<ecModels.EntityId>
  /**
   * Submits the current edit, whatever that may be
   *
   * Can use {@link ecModels.Target}, if needed
   *
   * @example
   * // Selects the current edit for the default target
   * { current: true }
   * // Selects the current edit for the given target
   * { current: { target: 'some-target '} }
   */
  current?: true | { readonly target: ecModels.Target }
}
export namespace EditsSelection {
  /**
   * Either a list of {@link ecModels.EntityId} or `true`, which indicates
   * all relevant {@link ecModels.EntityId} are being selected.
   */
  export type Of<TId extends ecModels.EntityId> = readonly TId[] | true

  /**
   * Provides a definite list of edits/deletions to submit, usually based on an
   * {@link EditsSelection}
   *
   * @see {@link EditsSelection.Result.from}
   */
  export interface Result<TTypeParams extends ecModels.TypeParams.Constraint> {
    creations: readonly {
      readonly id: CreationId
      readonly data: TTypeParams['createEntity']
    }[]
    updates: readonly {
      readonly id: ecModels.EntityId
      readonly data: TTypeParams['entity']
    }[]
    deletions: readonly ecModels.EntityId[]
  }
  export namespace Result {
    /**
     * Indicates a {@link EditsSelection.Result} has nothing selected
     */
    export function isEmpty({
      creations,
      deletions,
      updates,
    }: Readonly.Deep<Result<any>>): boolean {
      return (
        creations.length === 0 && deletions.length === 0 && updates.length === 0
      )
    }
  }
}
